<template>
  <div class="flex" :class="[
    { 'flex-col': !props.inline },
    { 'flex-row items-center': props.inline }
  ]">
    <div v-if="$slots.label" class="flex items-start">
      <label class="text-[13px] font-semibold text-foreground pl-0.5" :class="[{
        'mb-1.5': !props.inline,
        'mr-2': props.inline
      }]">{{ props.label }}</label>

      <Popper v-if="$slots.helperContent" :placement="helpPlacement"
        :arrow="true">
        <QuestionMarkCircleIcon class="w-5 h-5 text-muted-foreground ml-2" />
        <template #content>
          <div class="max-w-xs z-50 text-sm">
            <slot name="helperContent"></slot>
          </div>
        </template>
      </Popper>
    </div>

    <div class="flex items-center">
      <div class="relative">
        <input :name="props.name" :value="props.modelValue" @input="updateValue"
          type="number" :class="[wSizes[props.width]]"
          class="ig-input h-[46px] rounded-xl py-1.5 px-4 no-arrows text-[14px] font-medium bg-card text-foreground transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-apex-blue-500/40 focus:border-apex-blue-500" />

        <div
          class="absolute flex items-center justify-center top-0 h-full px-4 text-muted-foreground text-[13px]"
          :class="[
            props.position == 'left' ? 'left-0' : 'right-0',
          ]" v-if="groupType == 'text' && unit != ''">
          {{ props.unit }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Popper from 'vue3-popper';
import { Placement } from '@popperjs/core';
import { debounce } from 'lodash';
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline';

const props = withDefaults(defineProps<{
  label?: string
  name: string
  modelValue: number,
  unit?: string,
  type: 'text' | 'password' | 'number',
  groupType?: 'select' | 'text'
  position?: 'left' | 'right'
  inline?: boolean,
  helpPlacement?: Placement,
  width?: keyof typeof wSizes
}>(), {
  unit: '',
  groupType: 'text',
  position: 'right',
  inline: false,
  helpPlacement: 'auto',
  width: 'full'
});

const wSizes = {
  sm: 'w-20',
  md: 'w-28',
  lg: 'w-36',
  xl: 'w-44',
  '2xl': 'w-52',
  '3xl': 'w-60',
  '4xl': 'w-72',
  '5xl': 'w-80',
  '6xl': 'w-96',
  '7xl': 'w-120',
  '8xl': 'w-xl',
  '9xl': 'w-176',
  '10xl': 'w-208',
  'full': 'w-full',
};

const emit = defineEmits(['update:modelValue']);
const updateValue = (event: Event) => {
  const u = debounce(() => {
    const value = (event.target as HTMLInputElement).value;
    emit('update:modelValue', value ? Number(value) : 0);
  }, 500)
  u();
};
</script>

<style scoped>
/* ── Light mode ── */
.ig-input {
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}
.ig-input::placeholder {
  color: rgba(0, 0, 0, 0.32);
  font-weight: 400;
}

/* ── Dark mode ── */
:is(.dark *).ig-input {
  border: 1.5px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.04);
}
:is(.dark *).ig-input::placeholder {
  color: rgba(255, 255, 255, 0.30);
}

/* ── Hide number arrows ── */
input[type="number"].no-arrows::-webkit-inner-spin-button,
input[type="number"].no-arrows::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"].no-arrows {
  appearance: none;
  -moz-appearance: textfield;
  -webkit-appearance: none;
}
</style>
